import sha256 from 'sha256'

function debounce (func, wait, immediate) {
  let timeout
  return function () {
    const context = this
    const args = arguments
    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

const zeroPad = num => '00000000'.slice(String(num).length) + num

function stringToBinary (input) {
  return input.split('').map(
    char => zeroPad(char.charCodeAt(0).toString(2))
  ).join(' ')
}

function binaryToString (string) {
  return string.split(' ').map(num => String.fromCharCode(parseInt(num, 2))).join('')
}

function binaryToZeroWidth (binary) {
  return binary.split('').map((binaryNum) => {
    const num = parseInt(binaryNum, 10)
    if (num === 1) {
      return '​' // zero-width space
    } else if (num === 0) {
      return '‌' // zero-width non-joiner
    }
    return '‍' // zero-width joiner
  }).join('') // zero-width no-break space
}

const zeroWidthToBinary = string => (
  string.split('').map((char) => { // zero-width no-break space
    if (char === '​') { // zero-width space
      return '1'
    } else if (char === '‌') { // zero-width non-joiner
      return '0'
    }
    return ' ' // add single space
  }).join('')
)

export default ({ $publicAPI, $sentry, $log }, inject) => {
  const logger = $log.create('i18n')

  const translations = {}
  let storedTranslations = []

  function trackTranslation (hash, domain, input, args, path, found, locale) {
    // Don't track the same translation multiple times
    if (storedTranslations.includes(hash.slice(0, 10))) return

    translations[hash.slice(0, 10)] = { domain, input, args, path, found, locale }
    // storeTranslations() 02/08/2021 - disable translation storing as i18n is currently disabled
  }

  const storeTranslations = debounce(async function () {
    // Only store 1% of already-translated strings
    // and 20% of already-sent strings
    const newTranslations = Object.fromEntries(
      Object.entries(translations).filter(
        ([hash, details]) => {
          const alreadySent = storedTranslations.includes(hash)
          if (alreadySent && Math.random() >= 0.2) return false

          if (details.found) {
            // Translation was found, store 1%
            if (Math.random() <= 0.01) return true
          } else {
            // Translation not found, store 100%
            return true
          }
          return false
        }
      )
    )

    if (!Object.keys(newTranslations).length) return
    
    logger.debug(`${Object.keys(newTranslations).length} translations to store`)
    try {
      await $publicAPI.quietPost('i18n/store/', { translations: newTranslations })
    } catch (error) {
      logger.error(error)
      $sentry.captureException(error)
    }

    storedTranslations = storedTranslations.concat(Object.keys(translations))
  }, 1000)

  function translateWithIdentifier (input, ...args) {
    if (!input) return ''
    const domain = ((this || {}).$options || {})._componentTag ||
      ('_name' in this ? ((this || {})._name || '').replace(/[<>/]/g, '') : '') ||
      'unknown'

    /*
     * Calculate SHA-256 hash of domain-input, grab the first 8 chars and
     * translate into zero-width characters. We can then render the translated
     * string with a hidden identifier to aid translation tools.
     */
    const strippedInput = input.replace(/[\u200B-\u200D\uFEFF]/g, '')
    const hash = sha256(`${domain}.${strippedInput}`).slice(0, 8) // Use the first 8 chars of hash as key
    
    const missing = !this.$te(hash)
    const translated = missing
      ? this.$i18n._render(input, 'string', ...args)
      : this.$t(hash, ...args)

    trackTranslation(hash, domain, input, args, (this.$route || {}).path, !missing, this.$i18n.localeProperties.code)

    return this.$ta(translated, hash)
  }

  function translatePlural (singular, plural, subject, ...args) {
    if (Array.isArray(subject)) subject = subject.length
    if (subject === 1) return this.$tr(singular, ...args)
    return this.$tr(plural, ...args)
  }

  function getIdentifiers (input) {
    const rawIdentifier = binaryToString(zeroWidthToBinary(input.trim()))
    return [...new Set([...rawIdentifier.matchAll(/[a-f0-9]{8}/mg)].map(match => match[0]))]
  }

  function findElements (hash) {
    const search = binaryToZeroWidth(stringToBinary(hash))
    const results = []
    const xpathResult = document.evaluate(
      `//*[contains(text(),'${search}')]`,
      document,
      null,
      XPathResult.ORDERED_NODE_ITERATOR_TYPE,
      null
    )
    let node
    while ((node = xpathResult.iterateNext()) != null) {
      results.push(node)
    }
    return results
  }

  function currency (value, digits) {
    if (this.storeCurrency === 'GBP') return this.$ta(
      '£' + this.$n(value, { maximumFractionDigits: digits, minimumFractionDigits: digits }),
      '00000005'
    )

    return this.$ta(
      this.$n(value, { style: 'currency', maximumFractionDigits: digits, minimumFractionDigits: digits, currency: this.storeCurrency }),
      '00000001'
    )
  }

  function currencyShort (value) {
    return currency.bind(this)(value, 0)
  }

  function currencyLong (value) {
    return currency.bind(this)(value, 2)
  }

  function currencyAuto (value) {
    return Number.isInteger(value) ? currencyShort.bind(this)(value) : currencyLong.bind(this)(value)
  }

  function percentage (number, digits = 1) {
    return this.$ta(
      this.$n(number, { style: 'percent', maximumFractionDigits: digits, minimumFractionDigits: digits }),
      '00000003'
    )
  }

  function number (number, digits = 0) {
    return this.$ta(
      this.$n(number, { maximumFractionDigits: digits, minimumFractionDigits: digits }),
      '00000004'
    )
  }

  function annotate (string, hash) {
    const enabled = this.$store.getters['translations/showTranslationBanner']
    if (!enabled) return string
    return string + '﻿' + binaryToZeroWidth(stringToBinary(hash)) + '﻿'
  }

  inject('tr', translateWithIdentifier)
  inject('trp', translatePlural)
  inject('tca', currencyAuto)
  inject('tcs', currencyShort)
  inject('tcl', currencyLong)
  inject('tp', percentage)
  inject('tn', number)
  inject('ta', annotate)
  inject('i18nhelper', {
    translateWithIdentifier,
    zeroPad,
    stringToBinary,
    binaryToString,
    binaryToZeroWidth,
    zeroWidthToBinary,
    trackTranslation,
    storeTranslations,
    getIdentifiers,
    findElements,
    translations
  })
}